import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clientProgress, progressSummary, type ClientProgress, type Step } from '@/domain/workflow/progress';
import { getPref, setPref } from '@/platform/prefs';
import { useService } from '../hooks/useService';

/** 단계 이름을 막대 칸에 들어가게 짧게 */
const SHORT: Record<string, string> = {
  input: '입력',
  process: '처리',
  verify: '확인',
  ai: 'AI',
  review: '검토',
  journal: '기록지',
};

/** 펼쳤을 때 완료한 회기는 최근 몇 개만 (나머지는 "더 보기") */
const DONE_SHOWN = 4;

/** 지금 보고 있는 내담자 (내담자 화면이면 주소에서, 회기 화면이면 회기에서) */
export function useCurrentClientId(): string | null {
  const { pathname, search } = useLocation();
  const svc = useService();
  const [fromSession, setFromSession] = useState<{ sessionId: string; clientId: string } | null>(null);
  const c = /^\/clients\/([^/]+)/.exec(pathname)?.[1];
  const s = /^\/sessions\/([^/]+)/.exec(pathname)?.[1];
  const q = s === 'new' ? new URLSearchParams(search).get('clientId') : null;
  useEffect(() => {
    if (!s || s === 'new' || fromSession?.sessionId === s) return;
    let live = true;
    svc
      .getSession(s)
      .then((x) => live && setFromSession({ sessionId: s, clientId: x.clientId }))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [s, svc, fromSession?.sessionId]);
  if (c) return c;
  if (q) return q;
  if (s && fromSession?.sessionId === s) return fromSession.clientId;
  return null;
}

/**
 * 진행 상황 (내담자·회기 화면에서만). 회기 진행 화면은 자체 단계 막대가 있어 숨긴다.
 * - rail(PC): 오른쪽 여백에 세로로 — 다음 할 일, 바로가기, 회기별 단계(이름이 보이는 칸)
 * - 그 밖(폰·태블릿): 위에 한 줄(내담자·진행률·다음 할 일), 펼치면 같은 회기 목록
 */
export function ProgressDock({ rail = false }: { rail?: boolean }) {
  const clientId = useCurrentClientId();
  const svc = useService();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => getPref('ui.progressOpen') === '1');
  const [data, setData] = useState<{ clientId: string; code: string; p: ClientProgress } | null>(null);
  // 회기 진행 화면은 자체 단계 막대가 있어 겹치지 않게 숨긴다
  const onRun = /^\/sessions\/[^/]+\/run/.test(pathname);

  useEffect(() => {
    if (!clientId) return;
    let live = true;
    const load = async () => {
      try {
        const [client, sessions, documents, pendingReview] = await Promise.all([
          svc.getClient(clientId),
          svc.listSessions(clientId),
          svc.listDocuments(clientId),
          svc.pendingApprovals(clientId),
        ]);
        const p = clientProgress({
          clientId,
          sessions,
          documents,
          terminated: client.status === 'terminated',
          pendingReview,
        });
        if (live) setData({ clientId, code: client.clientCode, p });
      } catch {
        // 지운 내담자 등: 막대를 숨긴다 (아래 clientId 비교)
      }
    };
    void load();
    // 같은 화면에서 처리·반영·승인해도 막대가 바로 따라오도록: 회기·기록지·문서가 바뀌면 다시 읽는다
    let pending: number | undefined;
    const onChange = (e: Event) => {
      const type = (e as CustomEvent<{ entityType?: string }>).detail?.entityType;
      if (
        type &&
        ![
          'Session',
          'CounselingJournal',
          'DocumentArtifact',
          'Client',
          'RiskAssessment',
          'GoalProgress',
          'CaseFormulationRevision',
          'DiagnosticConsideration',
        ].includes(type)
      )
        return;
      window.clearTimeout(pending);
      pending = window.setTimeout(() => void load(), 300);
    };
    window.addEventListener('counselai:data-changed', onChange);
    // 다른 기기에서 동기화된 변경도 가끔 반영
    const t = window.setInterval(() => document.visibilityState === 'visible' && void load(), 15000);
    return () => {
      live = false;
      window.clearTimeout(pending);
      window.clearInterval(t);
      window.removeEventListener('counselai:data-changed', onChange);
    };
  }, [clientId, svc, pathname]);

  if ((onRun && !rail) || !clientId || !data || data.clientId !== clientId) return null;
  const { p, code } = data;
  const { pct, missed } = progressSummary(p);
  const toggle = () => {
    setOpen((o) => {
      setPref('ui.progressOpen', o ? '0' : '1');
      return !o;
    });
  };

  const nextLink = p.next && (
    <Link className={`button primary ${rail ? 'prail-next' : 'pdock-next'}`} to={p.next.path}>
      다음: {p.next.label} →
    </Link>
  );
  const links = (
    <>
      {!p.next?.path.startsWith('/sessions/new') && (
        <Link className="button pdock-btn" to={`/sessions/new?clientId=${clientId}`}>
          ＋ 새 회기
        </Link>
      )}
      <Link className="button pdock-btn" to={`/clients/${clientId}/brief`}>
        다음 회기 준비
      </Link>
      <Link className="button pdock-btn" to={`/clients/${clientId}/documents`}>
        문서 작성
      </Link>
    </>
  );
  const summary = (
    <>
      <span className="pdock-code">{code}</span>
      <span className="meter pdock-meter" aria-hidden>
        <span style={{ width: `${pct}%` }} />
      </span>
      <span className="pdock-pct">
        {pct}%{missed ? <span className="pdock-missed"> · 빠짐 {missed}</span> : null}
      </span>
    </>
  );

  if (rail)
    return (
      <aside className="prail no-print" aria-label="진행 상황">
        <div className="prail-head">{summary}</div>
        {nextLink}
        <div className="prail-links">{links}</div>
        <SessionList p={p} />
      </aside>
    );

  return (
    <div className="pdock no-print" aria-label="진행 상황">
      <div className="pdock-head">
        <button type="button" className="pdock-toggle" onClick={toggle} aria-expanded={open}>
          {summary}
          <span className="pdock-chev">{open ? '접기 ▴' : '회기별 ▾'}</span>
        </button>
        <div className="pdock-actions">
          {nextLink}
          {links}
        </div>
      </div>
      {open && (
        <div className="pdock-body">
          <SessionList p={p} grid />
        </div>
      )}
    </div>
  );
}

const STATE_KO: Record<Step['state'], string> = { done: '완료', current: '지금', missed: '빠짐', todo: '남음' };

/**
 * 회기 목록: 할 일이 남은 회기는 단계 이름이 보이는 칸으로, 끝난 회기는 한 줄로(최근 몇 개만, 나머지는 더 보기).
 */
function SessionList({ p, grid = false }: { p: ClientProgress; grid?: boolean }) {
  const [all, setAll] = useState(false);
  if (!p.sessions.length) return <p className="muted small">아직 회기가 없습니다.</p>;
  const finished = (steps: Step[]) => steps.every((x) => x.state === 'done');
  const open = p.sessions.filter((x) => !finished(x.steps));
  const done = p.sessions.filter((x) => finished(x.steps));
  const shownDone = all ? done : done.slice(-DONE_SHOWN);
  return (
    <div className="psl">
      {open.length > 0 && (
        <div className={grid ? 'psl-cards psl-grid' : 'psl-cards'}>
          {open.map(({ session, steps }) => {
            const st = steps.find((x) => x.state === 'missed') ?? steps.find((x) => x.state === 'current')!;
            return (
              <div key={session.id} className={`pcard ${st.state}`}>
                <Link className="pcard-head" to={st.path}>
                  <strong>{session.sessionNumber}회기</strong>
                  <span className="muted small">{session.sessionDate}</span>
                </Link>
                <Link className="pcard-what" to={st.path}>
                  {st.state === 'missed' ? '빠짐: ' : '다음: '}
                  {st.action} →
                </Link>
                <div className="pcard-steps">
                  {steps.map((x) => (
                    <Link
                      key={x.key}
                      to={x.path}
                      className={`pstep ${x.state}`}
                      title={`${x.label} — ${STATE_KO[x.state]}`}
                      aria-label={`${session.sessionNumber}회기 ${x.label} ${STATE_KO[x.state]}`}
                    >
                      {x.state === 'done' ? '✓ ' : x.state === 'missed' ? '! ' : ''}
                      {SHORT[x.key] ?? x.label}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {done.length > 0 && (
        <div className="psl-done">
          <span className="muted small">완료한 회기</span>
          {done.length > shownDone.length && (
            <button type="button" className="link small" onClick={() => setAll(true)}>
              앞의 {done.length - shownDone.length}개 더 보기
            </button>
          )}
          {shownDone.map(({ session }) => (
            <Link key={session.id} className="pdone" to={`/sessions/${session.id}`}>
              <span aria-hidden>✓</span> {session.sessionNumber}회기
              <span className="muted small">{session.sessionDate}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
